export const WATCH_PROGRESS_PENDING_KEY = 'luma_pending_watch_progress';
export const WATCH_PROGRESS_CACHE_KEY = 'luma_watch_progress_cache';

let lastProgressTimestamp = 0;

const getDefaultStorage = () => (
    typeof localStorage !== 'undefined' ? localStorage : null
);

const timestampValue = (value) => {
    const parsed = Date.parse(value || '');
    return Number.isFinite(parsed) ? parsed : 0;
};

const progressKey = (profileId, contentId) => `${profileId}:${contentId}`;

export const createProgressTimestamp = (afterTimestamp = null) => {
    const next = Math.max(
        Date.now(),
        lastProgressTimestamp + 1,
        timestampValue(afterTimestamp) + 1
    );
    lastProgressTimestamp = next;
    return new Date(next).toISOString();
};

export const readPendingProgressQueue = (storage = getDefaultStorage()) => {
    if (!storage) return [];
    try {
        const raw = storage.getItem(WATCH_PROGRESS_PENDING_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const readProgressCache = (storage = getDefaultStorage()) => {
    if (!storage) return [];
    try {
        const raw = storage.getItem(WATCH_PROGRESS_CACHE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const writeProgressCache = (items, storage = getDefaultStorage()) => {
    if (!storage) return;
    try {
        if (!items.length) {
            storage.removeItem(WATCH_PROGRESS_CACHE_KEY);
            return;
        }
        storage.setItem(WATCH_PROGRESS_CACHE_KEY, JSON.stringify(items.slice(-500)));
    } catch (error) {
        console.warn('[WatchHistory] Local resume cache write failed:', error);
    }
};

export const getCachedProgress = (profileId, contentId, storage = getDefaultStorage()) => {
    if (!profileId || !contentId) return null;
    const key = progressKey(profileId, contentId);
    return readProgressCache(storage).find(
        item => progressKey(item.profileId, item.payload?.content_id) === key
    )?.payload || null;
};

export const getCachedProgressItems = (profileId, storage = getDefaultStorage()) => (
    readProgressCache(storage)
        .filter(item => item.profileId === profileId && item.payload?.content_id)
        .map(item => item.payload)
);

export const cacheWatchProgress = (profileId, payload, storage = getDefaultStorage()) => {
    if (!profileId || !payload?.content_id) return null;
    const cache = readProgressCache(storage);
    const key = progressKey(profileId, payload.content_id);
    const existing = cache.find(item => progressKey(item.profileId, item.payload?.content_id) === key);
    const safePayload = {
        ...payload,
        // Never persist third-party stream credentials in long-lived browser storage.
        stream_url: String(payload.stream_url || '').startsWith('/api/') ? payload.stream_url : null
    };
    const freshest = pickFreshestProgress(existing?.payload, safePayload);
    const filtered = cache.filter(item => progressKey(item.profileId, item.payload?.content_id) !== key);
    filtered.push({ profileId, payload: freshest });
    writeProgressCache(filtered, storage);
    return freshest;
};

export const writePendingProgressQueue = (items, storage = getDefaultStorage()) => {
    if (!storage) return;
    try {
        if (!items.length) {
            storage.removeItem(WATCH_PROGRESS_PENDING_KEY);
            return;
        }
        storage.setItem(WATCH_PROGRESS_PENDING_KEY, JSON.stringify(items.slice(-100)));
    } catch (error) {
        console.warn('[WatchHistory] Local checkpoint write failed:', error);
    }
};

export const getPendingProgress = (profileId, contentId, storage = getDefaultStorage()) => {
    if (!profileId || !contentId) return null;
    const key = progressKey(profileId, contentId);
    return readPendingProgressQueue(storage).find(
        item => progressKey(item.profileId, item.payload?.content_id) === key
    ) || null;
};

export const queuePendingProgress = (profileId, payload, storage = getDefaultStorage()) => {
    if (!profileId || !payload?.content_id) return null;

    cacheWatchProgress(profileId, payload, storage);

    const queue = readPendingProgressQueue(storage);
    const key = progressKey(profileId, payload.content_id);
    const existing = queue.find(item => progressKey(item.profileId, item.payload?.content_id) === key);
    const incomingTimestamp = timestampValue(payload.last_watched_at);
    const existingTimestamp = timestampValue(existing?.payload?.last_watched_at);

    // A slow failed request must never replace a newer local checkpoint.
    if (existing && incomingTimestamp < existingTimestamp) return existing;

    const nextItem = {
        profileId,
        payload,
        queuedAt: createProgressTimestamp()
    };
    const filtered = queue.filter(item => progressKey(item.profileId, item.payload?.content_id) !== key);
    filtered.push(nextItem);
    writePendingProgressQueue(filtered, storage);
    return nextItem;
};

export const clearStoredProgress = (profileId, contentId, storage = getDefaultStorage()) => {
    if (!profileId || !contentId) return;
    const key = progressKey(profileId, contentId);
    writePendingProgressQueue(
        readPendingProgressQueue(storage).filter(
            item => progressKey(item.profileId, item.payload?.content_id) !== key
        ),
        storage
    );
    writeProgressCache(
        readProgressCache(storage).filter(
            item => progressKey(item.profileId, item.payload?.content_id) !== key
        ),
        storage
    );
};

export const acknowledgePendingProgress = (
    profileId,
    contentId,
    savedTimestamp,
    storage = getDefaultStorage()
) => {
    if (!profileId || !contentId) return;
    const queue = readPendingProgressQueue(storage);
    const key = progressKey(profileId, contentId);
    const savedAt = timestampValue(savedTimestamp);
    const filtered = queue.filter(item => {
        if (progressKey(item.profileId, item.payload?.content_id) !== key) return true;
        return timestampValue(item.payload?.last_watched_at) > savedAt;
    });
    writePendingProgressQueue(filtered, storage);
};

export const pickFreshestProgress = (...candidates) => candidates
    .filter(Boolean)
    .reduce((latest, candidate) => {
        if (!latest) return candidate;
        return timestampValue(candidate.last_watched_at) >= timestampValue(latest.last_watched_at)
            ? candidate
            : latest;
    }, null);
